import type { Coin } from './common';

export enum BridgeDirection {
  GRAVITY_TO_ETH = 'GRAVITY_TO_ETH',
  ETH_TO_GRAVITY = 'ETH_TO_GRAVITY',
}

export enum TransferStatus {
  PENDING = 'PENDING',
  BATCHED = 'BATCHED',
  RELAYED = 'RELAYED',
  CONFIRMED = 'CONFIRMED',
  FAILED = 'FAILED',
}

export enum BridgeSpeed {
  SLOW = 'Slow',
  FAST = 'Fast',
  SELF_RELAY = 'SelfRelay',
}

/**
 * Bridge fee tier from backend
 */
export interface BridgeFeeTier {
  speed: BridgeSpeed;
  bridge_fee_ugraviton: string;
  chain_fee_ugraviton: string;
  total_fee_ugraviton: string;
  estimated_time: string;
  label: string;
  description: string;
}

/**
 * Bridge fees response from backend
 */
export interface BridgeFees {
  slow: BridgeFeeTier;
  fast: BridgeFeeTier;
  self_relay: BridgeFeeTier;
  eth_gas_price_gwei: string;
  eth_price_usd?: string;
  grav_price_usd?: string;
}

/**
 * Bridge info from backend
 */
export interface BridgeInfo {
  gravity_bridge_contract: string;
  grav_denom: string;
  grav_erc20_address: string;
  chain_fee_rate: string;
  chain_fee_basis_points: number;
  eth_finality_blocks: number;
  eth_block_time_secs: number;
}

/**
 * Coin info for pending sends
 */
export interface CoinInfo {
  denom: string;
  amount: string;
}

/**
 * Pending send to Ethereum (from backend)
 */
export interface PendingSend {
  sender: string;
  eth_dest: string;
  amount: CoinInfo;
  bridge_fee: CoinInfo;
  tx_id?: string;
  status: 'Pending' | 'Batched' | 'Relayed';
}

/**
 * Pending sends response from backend
 */
export interface PendingSendsResponse {
  pending: PendingSend[];
  batched: PendingSend[];
}

/**
 * Pending batch info
 */
export interface PendingBatch {
  batch_nonce: number;
  token_contract: string;
  total_fee: CoinInfo;
  tx_count: number;
  batch_timeout: number;
}

export interface BatchTransaction {
  id: string;
  sender: string;
  dest_address: string;
  erc20_token: string;
  amount: string;
  erc20_fee: string;
}

export interface EthDeposit {
  sender: string;
  destination: string;
  amount: string;
  event_nonce: string;
  block_height: string;
  token_contract: string;
  tx_hash: string;
  confirmations: number;
  required_confirmations: number;
  status: TransferStatus;
}

export interface BridgeTransfer {
  id: string;
  direction: BridgeDirection;
  from: string;
  to: string;
  amount: Coin;
  fee: Coin;
  status: TransferStatus;
  created_at: string;
  completed_at?: string;
  tx_hash?: string;
  eth_tx_hash?: string;
}

/**
 * Parameters for creating a SendToEth message
 */
export interface SendToEthParams {
  sender: string;
  eth_dest: string;
  amount: string;
  bridge_fee: string;
  chain_fee: string;
  denom?: string;
}

// ============================================================================
// ETH → Gravity Deposit Types
// ============================================================================

/**
 * A pending deposit from Ethereum to Gravity Bridge
 */
export interface PendingDeposit {
  /** ERC20 token contract address */
  erc20: string;
  /** Ethereum sender address */
  sender: string;
  /** Gravity Bridge destination address */
  destination: string;
  /** Amount in token's smallest unit */
  amount: string;
  /** Event nonce for ordering */
  event_nonce: number;
  /** Block height where transaction was mined */
  block_height: number;
  /** Has finality been reached? */
  confirmed: boolean;
  /** Blocks remaining until confirmed */
  blocks_until_confirmed: number;
  /** Estimated seconds until confirmed */
  seconds_until_confirmed: number;
  /** Transaction hash on Ethereum */
  tx_hash?: string;
}

/**
 * Response for pending deposits query
 */
export interface PendingDepositsResponse {
  /** Latest Ethereum block height */
  latest_eth_block: number;
  /** Pending deposits for the queried address */
  deposits: PendingDeposit[];
}

/**
 * Parameters for creating a SendToCosmos transaction on Ethereum
 */
export interface SendToCosmosParams {
  /** ERC20 token contract address */
  token_contract: string;
  /** Gravity Bridge destination address (gravity1...) */
  destination: string;
  /** Amount in token's smallest unit (wei for 18 decimals) */
  amount: string;
}
